import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { getAuthToken, setAuthToken, removeAuthToken, getAuthUser, setAuthUser, removeAuthUser, migrateAuthFromLocalStorage } from '../utils/cookieUtils';
import { fetchCurrentUserProfile } from './api';

// Shared promise so React Strict Mode's double mount doesn't trigger two /user/profile requests
let sessionCheckPromise: Promise<Awaited<ReturnType<typeof fetchCurrentUserProfile>> | null> | null = null;
function getSessionCheckPromise(): Promise<Awaited<ReturnType<typeof fetchCurrentUserProfile>> | null> {
  if (!sessionCheckPromise) {
    sessionCheckPromise = fetchCurrentUserProfile()
      .then((data) => data)
      .catch(() => null);
  }
  return sessionCheckPromise;
}

export function resetSessionCheckPromise(): void {
  sessionCheckPromise = null;
}

export type AuthUser = {
  id?: number;
  username: string;
  name?: string;
  image?: string;
  description?: string;
  verified?: boolean;
  admin?: boolean;
};

type AuthContextValue = {
  token: string | null;
  user: AuthUser | null;
  isAuthenticated: boolean;
  login: (username: string, password: string) => Promise<void>;
  logout: () => void;
  setAuthData: (token: string, user: AuthUser) => void;
  updateUser: (userData: Partial<AuthUser> & { imageFile?: File }) => Promise<void>;
};

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

const API_BASE_URL = import.meta.env?.VITE_API_BASE_URL || 'http://localhost:9595';

const getApiUrl = () => API_BASE_URL;

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [token, setToken] = useState<string | null>(null);
  const [user, setUser] = useState<AuthUser | null>(null);

  useEffect(() => {
    migrateAuthFromLocalStorage();

    let cancelled = false;
    (async () => {
      // 1) Prefer server session: GET /user/profile (single request even under React Strict Mode).
      try {
        const data = await getSessionCheckPromise();
        if (cancelled) return;
        if (data) {
          const authUser: AuthUser = {
            id: data.id,
            username: data.username,
            name: data.name,
            image: data.image,
            description: data.description,
            verified: data.verified === true,
            admin: data.admin === true,
          };
          setUser(authUser);
          setToken(null);
          return;
        }
      } catch (_) {
        if (cancelled) return;
      }
      // 2) Fallback: restore from client-stored cookies (legacy token + user).
      const storedToken = getAuthToken();
      const storedUser = getAuthUser();
      if (storedToken) setToken(storedToken);
      if (storedUser) setUser(storedUser);
    })();
    return () => { cancelled = true; };
  }, []);

  const login = async (username: string, password: string) => {
    try {
      const res = await fetch(`${getApiUrl()}/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password }),
      });

      if (!res.ok) {
        const errorText = await res.text();
        if (import.meta.env.DEV) console.error('Login error:', res.status, errorText);
        if (res.status === 401) {
          throw new Error('نام کاربری یا رمز عبور اشتباه است');
        } else if (res.status >= 500) {
          throw new Error('خطا در سرور. لطفاً بعداً دوباره تلاش کنید');
        } else if (res.status === 0 || res.statusText === 'Failed to fetch') {
          throw new Error('خطا در اتصال به سرور. لطفاً اتصال اینترنت خود را بررسی کنید');
        } else {
          throw new Error('خطا در ورود به سیستم. لطفاً دوباره تلاش کنید');
        }
      }

      const data = await res.json();
      const authUser: AuthUser = {
        id: data.user_id,
        username: data.username,
        name: data.name,
        description: data.description,
        image: data.image,
        admin: data.admin === true,
      };
      setUser(authUser);
      setToken(data.token ?? null);
      // Backend may set HttpOnly cookie; if it returns a token, also store in client cookie for Bearer (e.g. cross-origin).
      if (data.token) {
        setAuthToken(data.token);
        setAuthUser(authUser);
      }
    } catch (error: any) {
      // Handle network errors, CORS, etc.
      if (error.name === 'TypeError' && error.message.includes('fetch')) {
        throw new Error('خطا در اتصال به سرور. لطفاً اتصال اینترنت خود را بررسی کنید');
      }
      
      // Re-throw the error if it's already a Persian message
      if (error.message && typeof error.message === 'string') {
        throw error;
      }
      
      // Default fallback
      throw new Error('خطا در ورود به سیستم. لطفاً دوباره تلاش کنید');
    }
  };

  const logout = async () => {
    try {
      await fetch(`${getApiUrl()}/logout`, { method: 'POST', credentials: 'include' });
    } catch {
      // Ignore; clear local state anyway
    }
    resetSessionCheckPromise();
    setToken(null);
    setUser(null);
    removeAuthToken();
    removeAuthUser();
  };

  const setAuthData = (newToken: string, newUser: AuthUser) => {
    setToken(newToken);
    setUser(newUser);
    setAuthToken(newToken);
    setAuthUser(newUser);
  };

  const updateUser = async (userData: Partial<AuthUser> & { imageFile?: File }) => {
    if (!user) {
      throw new Error('کاربر وارد نشده است');
    }

    const headers: Record<string, string> = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    try {
      let res: Response;

      if (userData.imageFile) {
        const formData = new FormData();
        formData.append('name', userData.name ?? user.name ?? '');
        formData.append('description', userData.description !== undefined ? userData.description : (user.description ?? ''));
        formData.append('username', userData.username !== undefined ? userData.username : (user.username ?? ''));
        formData.append('image', userData.imageFile);

        res = await fetch(`${getApiUrl()}/user/profile`, {
          method: 'PUT',
          credentials: 'include',
          headers,
          body: formData,
        });
      } else {
        const { imageFile, ...jsonData } = userData;
        res = await fetch(`${getApiUrl()}/user/profile`, {
          method: 'PUT',
          credentials: 'include',
          headers: { ...headers, 'Content-Type': 'application/json' },
          body: JSON.stringify(jsonData),
        });
      }

      if (!res.ok) {
        let errorMessage = 'خطا در به‌روزرسانی پروفایل';
        try {
          const errorData = await res.json();
          if (errorData.message) {
            errorMessage = errorData.message;
          }
        } catch {
          // If JSON parsing fails, try text
          const errorText = await res.text();
          if (errorText) {
            errorMessage = errorText;
          }
        }
        console.error('Update profile error:', res.status, errorMessage);
        
        if (res.status === 401) {
          throw new Error('احراز هویت نامعتبر است');
        } else if (res.status >= 500) {
          throw new Error(errorMessage || 'خطا در سرور. لطفاً بعداً دوباره تلاش کنید');
        } else {
          throw new Error(errorMessage);
        }
      }

      const updatedData = await res.json();
      const updatedUser: AuthUser = {
        ...user,
        id: updatedData.user_id || user.id,
        username: updatedData.username || user.username,
        description: updatedData.description !== undefined && updatedData.description !== null ? updatedData.description : (user.description || ''),
        name: updatedData.name || userData.name || user.name,
        image: updatedData.image || user.image,
      };

      setUser(updatedUser);
      if (token) setAuthUser(updatedUser);
    } catch (error: any) {
      if (error.name === 'TypeError' && error.message.includes('fetch')) {
        throw new Error('خطا در اتصال به سرور. لطفاً اتصال اینترنت خود را بررسی کنید');
      }
      
      if (error.message && typeof error.message === 'string') {
        throw error;
      }
      
      throw new Error('خطا در به‌روزرسانی پروفایل');
    }
  };

  const value = useMemo<AuthContextValue>(() => ({
    token,
    user,
    isAuthenticated: Boolean(user ?? (token && token.length > 0)),
    login,
    logout,
    setAuthData,
    updateUser,
  }), [token, user]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextValue => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider');
  return ctx;
};
